import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS } from '../../../styles/theme';
import type { AdminDialogProps, CreateDroneResponse } from '../types/adminTools.types';

interface AddDroneDialogProps extends AdminDialogProps {
  serialNumber: string;
  model: string;
  loading: boolean;
  result: CreateDroneResponse | null;
  error: string;
  onAddDrone: () => Promise<void>;
  onSerialNumberChange: (value: string) => void;
  onModelChange: (value: string) => void;
}

export const AddDroneDialog: React.FC<AddDroneDialogProps> = ({
  open,
  onClose,
  serialNumber,
  model,
  loading,
  result,
  error,
  onAddDrone,
  onSerialNumberChange,
  onModelChange,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="add-drone-dialog">
      <DialogTitle>{t('adminToolsPage.addDrone.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('adminToolsPage.addDrone.serialNumber')}
            value={serialNumber}
            onChange={(e) => onSerialNumberChange(e.target.value)}
            margin="normal"
            placeholder={t('adminToolsPage.addDrone.serialNumberPlaceholder')}
            disabled={loading}
            inputProps={{ 'data-testid': 'add-drone-serial-input' }}
          />

          <TextField
            fullWidth
            label={t('adminToolsPage.addDrone.model')}
            value={model}
            onChange={(e) => onModelChange(e.target.value)}
            margin="normal"
            placeholder={t('adminToolsPage.addDrone.modelPlaceholder')}
            disabled={loading}
            inputProps={{ 'data-testid': 'add-drone-model-input' }}
          />

          {result && (
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {result.message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            • {t('adminToolsPage.addDrone.notes.uniqueSerial')}
            <br />• {t('adminToolsPage.addDrone.notes.assignedToXer')}
            <br />• {t('adminToolsPage.addDrone.notes.activeByDefault')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {result?.success ? t('adminToolsPage.close') : t('adminToolsPage.cancel')}
        </Button>
        {!result?.success && (
          <Button
            onClick={onAddDrone}
            variant="contained"
            disabled={loading || !serialNumber.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : <Add />}
            data-testid="add-drone-submit-btn"
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: BRAND_COLORS.forestGreen.main,
                opacity: 0.8,
              },
            }}
          >
            {loading ? t('adminToolsPage.addDrone.adding') : t('adminToolsPage.addDrone.addDrone')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
